import { useState, useEffect, useRef, useCallback } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { X, Eye, EyeOff, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { findVinConflict } from '@/lib/vinCheck';
import { TRUCK_TYPES, TRAILER_TYPES } from '@/lib/equipmentTypes';
import DQFChecklist from './DQFChecklist';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const DISCARD_PROMPT =
  'You have unsaved driver information. Discard your changes and leave?';

// Shallow equality on the form objects. Adequate here -- the form is
// a flat string/number/boolean record, no nested objects.
const sameShape = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const SectionHeader = ({ title }) => (
  <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3 pb-1 border-b border-border">{title}</p>
);

// Set of values that USED TO live in truck_type but were actually
// freight/trailer types under the old schema. When loading an existing
// driver we auto-migrate these into trailer_type and reset truck_type
// to a sensible default, so the UI shows semantically-correct fields.
const LEGACY_TRAILER_VALUES_AS_TRUCK_TYPE = new Set(TRAILER_TYPES);

// USPS / postal abbreviations for the 50 states + DC. Used by both
// the residential Address state dropdown and the CDL State dropdown
// (CDL state of issuance, per 49 CFR 383.71 / FMCSA Clearinghouse
// reporting requirements). Sorted A-Z for predictable scanning.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

export default function DriverFormModal({ driver, onClose, onSaved, initialTab = 'profile' }) {
  const { staffRole } = useAuth();
  const canEditEmployeeNumber = staffRole === 'hr' || staffRole === 'super_admin';
  const [showAccount, setShowAccount] = useState(false);
  const [tab, setTab] = useState(driver ? initialTab : 'profile');
  const [form, setForm] = useState({
    full_name: '',
    employee_number: '',
    driver_type: 'Company Driver',
    company_name: '',
    user_email: '',
    phone: '',
    address: '',                    // street line
    city: '',
    state: '',                      // residential state (USPS abbrev, dropdown-controlled)
    zip: '',
    truck_type: 'Sleeper',         // tractor / power unit (was 'Dry Van' under legacy schema)
    cdl_number: '',
    cdl_state: '',                  // CDL state of issuance (USPS abbrev, dropdown-controlled)
    bank_routing_number: '',
    bank_account_number: '',
    truck_year: '',
    truck_make: '',
    truck_model: '',
    truck_info: '',
    engine_specs: '',
    truck_vin: '',
    license_plate: '',
    trailer_type: 'Dry Van',        // freight equipment -- drives market load matching
    trailer_year: '',
    trailer_make: '',
    trailer_model: '',
    trailer_info: '',
    trailer_vin: '',
    trailer_size: '',
    // Number inputs below are kept as '' (not 0) until the admin actually
    // types something -- a numeric 0 default meant the field always
    // rendered "0", and clearing it to type a real value snapped straight
    // back to "0" on every keystroke (parseFloat('') || 0 === 0), so a new
    // digit landed AFTER that phantom 0 instead of replacing it. Coerced
    // to a real number only at save time.
    weekly_truck_payment: '',
    weekly_trailer_payment: '',
    // Company Driver pay model -- 'mileage' ($/mi × load miles) or
    // 'percentage' (% of load gross revenue). Not applicable to Owner
    // Operators, who are paid gross revenue minus Carrier Take % instead
    // (see Pay & Deductions). Left unset, markLoadDelivered falls back to
    // the pre-existing gross-minus-carrier-take formula so an
    // already-active company driver's pay doesn't silently drop to $0
    // the moment this ships -- it only takes effect once a rate is
    // actually configured here.
    pay_type: 'mileage',
    pay_per_mile: '',
    pay_percentage: '',
    is_active: true,
    hire_date: '',
    termination_date: '',
    termination_reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const isCompanyDriver = form.driver_type === 'Company Driver';

  // Snapshot the form's initial state so we can detect "dirty" later.
  // - CREATE mode (no driver prop): baseline is the empty defaults
  //   declared above; dirty = the user typed ANYTHING.
  // - EDIT mode (driver prop): the useEffect below overwrites this ref
  //   with the loaded driver values; dirty = user changed any field.
  // Using a ref (not state) avoids unnecessary re-renders -- the
  // baseline never needs to trigger a re-render itself, only the live
  // form does. After a successful save we also reset this ref so the
  // dirty check goes false again before onClose fires.
  const initialFormRef = useRef(null);
  if (initialFormRef.current === null) {
    initialFormRef.current = JSON.parse(JSON.stringify(form));
  }
  const isDirty = !sameShape(form, initialFormRef.current);

  useEffect(() => {
    if (driver) {
      // Legacy-schema auto-migration: drivers stored before the
      // truck_type / trailer_type split had truck_type='Dry Van' /
      // 'Reefer' / etc., which were really trailer values masquerading
      // as truck values. Detect those, route them into trailer_type,
      // and reset truck_type to a sensible default. Admin sees correct
      // semantics; the next save persists the migration without
      // requiring a DB-level backfill.
      const looksLikeLegacy = LEGACY_TRAILER_VALUES_AS_TRUCK_TYPE.has(driver.truck_type);
      const loaded = {
        full_name: driver.full_name || '',
        employee_number: driver.employee_number || '',
        driver_type: driver.driver_type || 'Company Driver',
        company_name: driver.company_name || '',
        user_email: driver.user_email || '',
        phone: driver.phone || '',
        address: driver.address || '',
        city: driver.city || '',
        state: driver.state || '',
        zip: driver.zip || '',
        truck_type: looksLikeLegacy ? 'Sleeper' : (driver.truck_type || 'Sleeper'),
        cdl_number: driver.cdl_number || '',
        cdl_state: driver.cdl_state || '',
        bank_routing_number: driver.bank_routing_number || '',
        bank_account_number: driver.bank_account_number || '',
        truck_year: driver.truck_year || '',
        truck_make: driver.truck_make || '',
        truck_model: driver.truck_model || '',
        truck_info: driver.truck_info || '',
        engine_specs: driver.engine_specs || '',
        truck_vin: driver.truck_vin || '',
        license_plate: driver.license_plate || '',
        trailer_type: driver.trailer_type || (looksLikeLegacy ? driver.truck_type : 'Dry Van'),
        trailer_year: driver.trailer_year || '',
        trailer_make: driver.trailer_make || '',
        trailer_model: driver.trailer_model || '',
        trailer_info: driver.trailer_info || '',
        trailer_vin: driver.trailer_vin || '',
        trailer_size: driver.trailer_size || '',
        weekly_truck_payment: driver.weekly_truck_payment || '',
        weekly_trailer_payment: driver.weekly_trailer_payment || '',
        pay_type: driver.pay_type || 'mileage',
        pay_per_mile: driver.pay_per_mile || '',
        pay_percentage: driver.pay_percentage || '',
        is_active: driver.is_active !== false,
        hire_date: driver.hire_date || '',
        termination_date: driver.termination_date || '',
        termination_reason: driver.termination_reason || '',
      };
      setForm(loaded);
      // Edit-mode baseline: dirty becomes true only when the user
      // changes a loaded field, not just because driver data exists.
      initialFormRef.current = JSON.parse(JSON.stringify(loaded));
    }
  }, [driver]);

  // Company drivers run company-owned equipment -- assigned from the
  // Assets tab (admin/HR only), not typed in here. Look up whatever's
  // currently assigned to them so this modal can show it read-only.
  // Owner Operators skip this entirely; they still fill in their own
  // truck/trailer details below since that's what the weekly equipment
  // payment deductions are for.
  useEffect(() => {
    if (!driver?.user_email || !isCompanyDriver) { setAssignedAssets([]); return; }
    setLoadingAssets(true);
    client.entities.Asset.filter({ assigned_driver_email: driver.user_email }, '-created_date', 10)
      .then(setAssignedAssets)
      .catch(() => setAssignedAssets([]))
      .finally(() => setLoadingAssets(false));
  }, [driver?.user_email, isCompanyDriver]);

  // -------- close + navigation guard --------
  // Wraps every close path so the modal can't be dismissed (backdrop,
  // X button, Cancel, Escape, OR sidebar tab navigation) while the
  // form has unsaved input -- without confirmation. Marcus reported
  // losing a long Add-Driver form after accidentally clicking a
  // sidebar tab; this prevents the data loss.
  const closeWithGuard = useCallback(() => {
    if (!isDirty) { onClose(); return; }
    if (window.confirm(DISCARD_PROMPT)) onClose();
  }, [isDirty, onClose]);

  // (Removed: useBlocker-based React Router navigation guard.
  // useBlocker requires a data router (createBrowserRouter +
  // RouterProvider) but App.jsx uses the legacy <BrowserRouter>,
  // so calling it crashed the modal to a black screen when opened.
  // The backdrop / X / Escape / Cancel guards below + the silent
  // refresh fix in AuthContext cover the practical cases: form data
  // now survives browser-tab focus changes, and the user can't
  // accidentally dismiss the modal by clicking around inside it.
  // The remaining edge case -- clicking a TMS sidebar section while
  // the modal is open -- still unmounts, but it requires deliberate
  // intent (no easy accidental path), and a future fix can route
  // that case through a custom click interceptor without depending
  // on react-router-dom data-router features.)

  // Escape closes the modal. Run through closeWithGuard so it asks
  // for confirmation when dirty rather than nuking unsaved input.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeWithGuard(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeWithGuard]);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    // Truck Type is only required for an Owner Operator, who types it in
    // themselves below. A Company Driver's truck_type is now synced from
    // whatever Asset is actually assigned to them (AssetsTab.jsx) -- it's
    // legitimately blank until a truck is assigned, same as their
    // Equipment Info section already shows "Not yet assigned" for.
    if (!form.full_name || (!isCompanyDriver && !form.truck_type)) {
      toast.error('Full name and truck type are required.');
      return;
    }
    setSaving(true);
    // Block two driver records sharing the same email -- every lookup
    // in the app (My Profile, Load Board, Financials, invite accept) keys
    // off user_email with no uniqueness enforced at the DB level, so two
    // drivers on the same email silently collide: whichever record was
    // updated more recently wins everywhere, and the other becomes an
    // invisible duplicate that resurfaces confusingly (e.g. looking
    // "un-deleted") the moment the visible one is removed.
    const normalizedEmail = (form.user_email || '').trim().toLowerCase();
    if (normalizedEmail) {
      const allProfiles = await client.entities.DriverProfile.list('-created_date', 200).catch(() => []);
      const conflict = allProfiles.find(p =>
        p.id !== driver?.id && p.user_email?.toLowerCase() === normalizedEmail
      );
      if (conflict) {
        toast.error(`A driver with this email already exists: ${conflict.full_name}.`);
        setSaving(false);
        return;
      }
    }
    // truck_vin/trailer_vin are only user-editable here for Owner
    // Operators (see the truck_type ? ... : field('Truck VIN', ...)
    // branch below) -- for company drivers these two fields are a
    // read-only mirror of whatever Asset is currently assigned (synced
    // in by AssetsTab.jsx's handleSave), never touched by this form.
    // Checking them here regardless of driver_type meant a company
    // driver's own already-assigned truck/trailer VIN was checked
    // against Asset records with no excludeAssetId to exclude the
    // asset that's SUPPOSED to hold it -- every save of a company
    // driver with an assigned truck falsely reported "VIN ... is
    // already on asset Unit X", even though that's the correct,
    // intended assignment.
    if (!isCompanyDriver) {
      if (form.truck_vin) {
        const c = await findVinConflict(form.truck_vin, { excludeProfileId: driver?.id });
        if (c) { toast.error(c); setSaving(false); return; }
      }
      if (form.trailer_vin) {
        const c = await findVinConflict(form.trailer_vin, { excludeProfileId: driver?.id });
        if (c) { toast.error(c); setSaving(false); return; }
      }
    }
    const saveData = {
      ...form,
      // Every downstream lookup (DriverProfile.jsx, LoadBoard.jsx, Financials.jsx,
      // the invite accept flow) keys off user_email with no user_id fallback --
      // whatever casing gets typed here becomes the row's permanent identity.
      // Left as-typed, an admin entering "Marcus@Co.com" created a row that
      // silently never matched that driver's actual (lowercased) auth session
      // email anywhere in the app. Every write path normalizes to lowercase
      // (create_invitation does the same), so this is the one place that needs
      // fixing to keep new/edited rows consistent going forward.
      user_email: (form.user_email || '').trim().toLowerCase(),
      truck_info: [form.truck_year, form.truck_make, form.truck_model].filter(Boolean).join(' ') || form.truck_info,
      trailer_info: [form.trailer_year, form.trailer_make, form.trailer_model].filter(Boolean).join(' ') || form.trailer_info,
      // hire_date/termination_date are real `date`/`timestamptz` columns --
      // an empty string is invalid input for those types, so send null.
      hire_date: form.hire_date || null,
      termination_date: form.termination_date || null,
      // Weekly Payment Deductions (below) only renders for Owner
      // Operators now -- zero these out on save for a company driver so
      // switching a driver from O/O to Company also clears any leftover
      // nonzero payment from before, instead of it silently continuing
      // to deduct from a section the admin can no longer even see.
      // These four are kept as '' in form state while being typed (see the
      // form defaults above) so the field can actually be cleared -- coerce
      // to a real number here, at save time.
      weekly_truck_payment: isCompanyDriver ? 0 : parseFloat(form.weekly_truck_payment) || 0,
      weekly_trailer_payment: isCompanyDriver ? 0 : parseFloat(form.weekly_trailer_payment) || 0,
      // Pay Rate (below) only renders for Company Drivers -- same
      // zero-on-switch reasoning as the weekly payments above, so an
      // Owner Operator never carries a stale per-mile/percentage rate.
      pay_type: isCompanyDriver ? form.pay_type : 'mileage',
      pay_per_mile: isCompanyDriver ? parseFloat(form.pay_per_mile) || 0 : 0,
      pay_percentage: isCompanyDriver ? parseFloat(form.pay_percentage) || 0 : 0,
      // Same reasoning as the weekly payments above -- Company Name is
      // an O/O's own business entity, hidden from the form above for a
      // company driver, but was never actually cleared on save, so it
      // kept showing up on that driver's settlement PDFs/payment
      // records (RecordPaymentModal.jsx, RegeneratePdfButton.jsx both
      // read it) as a stale "DBA: <old company>" line.
      company_name: isCompanyDriver ? '' : form.company_name,
      // dispatch_service has no field anywhere in THIS form (it's a
      // driver self-service toggle, set from DriverProfile.jsx) --
      // `...form` above never includes it, so a plain update() here
      // never touched it, and an admin switching a driver from Owner
      // Operator to Company Driver had no way to turn it off. Left on,
      // ActiveLoad.jsx keeps deducting a real dispatch fee from that
      // driver's settlements indefinitely. Force it off for a company
      // driver; leave it untouched for an O/O (this form has no
      // control for it either way, so don't send a value that could
      // clobber what the driver set themselves).
      ...(isCompanyDriver ? { dispatch_service: false } : {}),
    };
    try {
      if (driver) {
        await client.entities.DriverProfile.update(driver.id, saveData);
        toast.success('Driver profile updated.');
        // Reset dirty baseline BEFORE onClose -- otherwise the close
        // path would prompt "discard unsaved changes?" on a
        // just-successfully-saved form.
        initialFormRef.current = JSON.parse(JSON.stringify(form));
        onSaved(saveData);
        onClose();
      } else {
        // Every driver must complete DQF before going active -- create
        // them as Pending so they route to the Approvals tab.
        await client.entities.DriverProfile.create({
          ...saveData,
          approval_status: 'Pending',
          is_active: false,
          onboarding_complete: true,
        });
        toast.success('Driver created. Complete DQF in the Approvals tab before activating.');
        // Best-effort, silent when no email was entered -- Email Address
        // isn't a required field here (some drivers get added as pure
        // paper HR records with no app access at all), so a missing
        // email on a brand new driver isn't something to flag as an
        // error the moment they're created.
        if (saveData.user_email) sendDriverInvite(saveData.user_email, { silent: true });
        initialFormRef.current = JSON.parse(JSON.stringify(form));
        onSaved();
        onClose();
      }
    } catch (e) {
      console.error('Save failed:', e);
      toast.error('Save failed. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  // This form only ever creates/edits the HR-side profile record (see
  // handleSave above) -- it never touches Supabase Auth, so a driver
  // added here has no way to actually log into the app on its own.
  // Sending an invite is the separate, real path to that (same
  // create_invitation RPC InvitationsSection.jsx uses on the Invitations
  // tab, just triggered from this driver's own record instead of typed
  // fresh on a different screen). Errors are reported but never thrown --
  // callers treat this as best-effort (a driver record with no working
  // invite is still a valid HR record; the driver just won't have app
  // access until someone retries).
  // Onboarding.jsx already looks up an existing DriverProfile by email
  // before creating a new one (see its `existingProfile` check), so a
  // driver who accepts and completes their application there updates
  // THIS record rather than leaving it orphaned next to a duplicate.
  const sendDriverInvite = async (rawEmail, { silent = false } = {}) => {
    const email = (rawEmail || '').trim().toLowerCase();
    if (!email) {
      if (!silent) toast.error('This driver has no email on file to invite.');
      return;
    }
    try {
      const invitation = await client.rpc('create_invitation', { p_email: email, p_role: 'driver' });
      const acceptLink = `${window.location.origin}/accept-invite?token=${invitation.token}`;
      try {
        await navigator.clipboard.writeText(acceptLink);
        toast.success(`Invite sent to ${email} — accept link copied to clipboard.`);
      } catch {
        toast.success(`Invite sent to ${email}.`);
      }
    } catch (err) {
      toast.error(err?.message || `Could not send invite to ${email}.`);
    }
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    try {
      await sendDriverInvite(driver?.user_email);
    } finally {
      setSendingInvite(false);
    }
  };

  const field = (label, key, type = 'text', autoComplete) => (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Input
        type={type}
        value={form[key] ?? ''}
        onChange={e => set(key, e.target.value)}
        autoComplete={autoComplete}
        className="text-foreground"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeWithGuard} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-xl my-8 p-6 space-y-5 z-10">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground">
            {driver ? 'Edit Driver Profile' : 'Add New Driver'}
          </h2>
          <div className="flex items-center gap-3">
            {/* This form only creates/edits the HR record -- it never
                gives the driver actual app login. Only shown once a
                driver record exists (has an email to invite). */}
            {driver && (
              <button
                onClick={handleSendInvite}
                disabled={sendingInvite}
                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                title="Email this driver an invite to sign in and use the app"
              >
                {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {sendingInvite ? 'Sending…' : 'Send App Invite'}
              </button>
            )}
            <button onClick={closeWithGuard} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-border -mt-1">
          <button
            onClick={() => setTab('profile')}
            className={cn("px-4 py-2 text-sm font-medium transition-colors", tab === 'profile' ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground")}
          >
            Profile
          </button>
          {driver && (
            <button
              onClick={() => setTab('dqf')}
              className={cn("px-4 py-2 text-sm font-medium transition-colors", tab === 'dqf' ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground")}
            >
              DQF
            </button>
          )}
        </div>

        {tab === 'profile' && (
        <>
        {/* Contact Information */}
        <div>
          <SectionHeader title="Contact Information" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('Full Name *', 'full_name')}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Employee #</label>
              <Input
                value={form.employee_number ?? ''}
                onChange={e => set('employee_number', e.target.value)}
                readOnly={!canEditEmployeeNumber}
                className={cn("text-foreground", !canEditEmployeeNumber && "opacity-70 cursor-not-allowed")}
                placeholder={canEditEmployeeNumber ? 'e.g. 48001' : '—'}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Role *</label>
              <select
                value={form.driver_type}
                onChange={e => set('driver_type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
              >
                <option value="Company Driver" className="bg-background">Company Driver</option>
                <option value="Owner Operator" className="bg-background">Owner Operator</option>
              </select>
            </div>
            {/* Owner Operator only -- their own business entity, not
                relevant to an employee. Previously shown regardless of
                driver_type, so a driver switched from O/O to Company
                still saw (and could keep re-saving) their old company
                name here with no indication it was stale/no longer
                applicable. */}
            {!isCompanyDriver && field('Company Name (LLC/Corp)', 'company_name')}
            {field('Email Address', 'user_email', 'email')}
            {field('Phone Number', 'phone', 'tel')}
            {/* Structured address fields. Replaced a single freeform
                "Address" textbox so downstream consumers (PDFs,
                mapping APIs, settlement docs) can read clean city /
                state / zip components instead of regex-parsing a
                blob. Legacy drivers with their old full-address string
                will see it sitting in the Street field on edit; admin
                can split city/state/zip into their own fields on the
                next save without losing any data. */}
            <div className="sm:col-span-2">
              <TrimbleAddressAutocomplete
                value={form.address ?? ''}
                onChange={e => set('address', e.target.value)}
                onSelect={data => {
                  set('address', data.street || form.address);
                  if (data.city) set('city', data.city);
                  if (data.state) set('state', data.state);
                  if (data.zip) set('zip', data.zip);
                }}
                label="Street Address"
                placeholder="1234 Main St"
              />
            </div>
            {field('City', 'city')}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">State</label>
              <select
                value={form.state}
                onChange={e => set('state', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
              >
                <option value="" className="bg-background">—</option>
                {US_STATES.map(s => (
                  <option key={s} value={s} className="bg-background">{s}</option>
                ))}
              </select>
            </div>
            {field('ZIP', 'zip')}
          </div>
        </div>

        {/* Credentials & Payment */}
        <div>
          <SectionHeader title="Credentials & Payment" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CDL Number + state of issuance. CDLs are state-issued
                (49 CFR 383.71) and the state matters for FMCSA
                Clearinghouse + DQF compliance -- can't derive it from
                a license number alone. */}
            <div className="sm:col-span-2 grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">CDL Number</label>
                <Input
                  type="text"
                  value={form.cdl_number ?? ''}
                  onChange={e => set('cdl_number', e.target.value)}
                  className="text-foreground"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">CDL State</label>
                <select
                  value={form.cdl_state}
                  onChange={e => set('cdl_state', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
                >
                  <option value="" className="bg-background">—</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s} className="bg-background">{s}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* autoComplete="off" on both -- neither has a real autocomplete
                purpose, but leaving it unset let the browser's autofill
                guess at them (e.g. a saved email), which then got typed
                straight into real bank fields on Save. */}
            {field('Bank Routing Number', 'bank_routing_number', 'text', 'off')}
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Bank Account Number</label>
              <div className="relative">
                <Input
                  type={showAccount ? 'text' : 'password'}
                  autoComplete="off"
                  value={form.bank_account_number ?? ''}
                  onChange={e => set('bank_account_number', e.target.value)}
                  className="text-foreground pr-10"
                  placeholder="Enter account number"
                />
                <button type="button" onClick={() => setShowAccount(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showAccount ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Info */}
        <div>
          <SectionHeader title="Equipment Info" />
          {isCompanyDriver ? (
            <div className="space-y-2.5">
              <p className="text-xs text-muted-foreground">
                Company drivers run company-owned equipment. Assign or swap their truck/trailer from the Assets tab -- Admin and HR only, not editable here.
              </p>
              {loadingAssets ? (
                <div className="text-sm text-muted-foreground py-2">Loading assigned equipment…</div>
              ) : assignedAssets.length > 0 ? (
                <div className="space-y-2">
                  {assignedAssets.map(a => (
                    <div key={a.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-transparent border border-input">
                      <div>
                        <p className="text-sm text-foreground">{a.asset_type} · Unit {a.unit_number}</p>
                        {a.vin && <p className="text-xs text-muted-foreground">VIN {a.vin}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground">{a.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-2 px-3 rounded-lg bg-transparent border border-input">
                  {driver ? 'No truck or trailer assigned yet.' : 'Assign equipment from the Assets tab once this driver is added.'}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Truck Type *</label>
                <p className="text-[10px] text-muted-foreground mb-1.5">The tractor / power unit -- NOT the trailer.</p>
                <select
                  value={form.truck_type}
                  onChange={e => set('truck_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
                >
                  {TRUCK_TYPES.map(t => (
                    <option key={t} value={t} className="bg-background">{t}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {field('Truck Year', 'truck_year')}
                {field('Truck Make', 'truck_make')}
                {field('Truck Model', 'truck_model')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('Engine Specs', 'engine_specs')}
                {field('Truck VIN', 'truck_vin')}
                {field('License Plate', 'license_plate')}
              </div>
              <p className="text-xs font-semibold text-muted-foreground pt-2">Trailer (optional)</p>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Trailer Type</label>
                <p className="text-[10px] text-muted-foreground mb-1.5">Freight equipment -- drives market load matching.</p>
                <select
                  value={form.trailer_type}
                  onChange={e => set('trailer_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
                >
                  {TRAILER_TYPES.map(t => (
                    <option key={t} value={t} className="bg-background">{t}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {field('Trailer Year', 'trailer_year')}
                {field('Trailer Make', 'trailer_make')}
                {field('Trailer Model', 'trailer_model')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('Trailer VIN', 'trailer_vin')}
                {field('Trailer Size (e.g. 53 ft)', 'trailer_size')}
              </div>
            </div>
          )}
        </div>

        {/* Weekly Payments -- Owner Operators only. Company drivers run
            company-owned equipment (assigned/read-only above); there's no
            personal truck/trailer financing to deduct from their pay, so
            this section previously showing regardless of driver_type was
            misleading (and handleSave now zeroes both fields out on save
            for a company driver regardless, so a stale value from before
            a driver_type switch can't silently keep deducting). */}
        {!isCompanyDriver && (
          <div>
            <SectionHeader title="Weekly Payment Deductions" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Weekly Truck Payment ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.weekly_truck_payment}
                  onChange={e => set('weekly_truck_payment', e.target.value)}
                  className="text-foreground"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave 0 if truck is owned outright</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Weekly Trailer Payment ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.weekly_trailer_payment}
                  onChange={e => set('weekly_trailer_payment', e.target.value)}
                  className="text-foreground"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave 0 if no trailer payment</p>
              </div>
            </div>
          </div>
        )}

        {/* Pay Rate -- Company Drivers only. Owner Operators are paid
            gross revenue minus Carrier Take % (Pay & Deductions); a
            Company Driver isn't splitting load revenue with the
            carrier at all, they're paid a flat rate -- either $/mile
            or a % of the load's gross. Leaving both at 0 falls back to
            the old gross-minus-carrier-take formula (see markLoadDelivered
            in ActiveLoad.jsx) so an existing driver's pay doesn't drop to
            $0 the moment this ships. */}
        {isCompanyDriver && (
          <div>
            <SectionHeader title="Pay Rate" />
            <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit mb-3">
              <button
                type="button"
                onClick={() => set('pay_type', 'mileage')}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  form.pay_type === 'mileage' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Per Mile
              </button>
              <button
                type="button"
                onClick={() => set('pay_type', 'percentage')}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  form.pay_type === 'percentage' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Percentage
              </button>
            </div>
            {form.pay_type === 'percentage' ? (
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Pay Percentage (% of load gross)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.pay_percentage}
                  onChange={e => set('pay_percentage', e.target.value)}
                  className="text-foreground max-w-[200px]"
                  placeholder="0.0"
                />
                <p className="text-xs text-muted-foreground mt-1">Driver pay = this % of each load's gross revenue, minus any fuel advance/lumper/late fee deductions.</p>
              </div>
            ) : (
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Pay Per Mile ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.pay_per_mile}
                  onChange={e => set('pay_per_mile', e.target.value)}
                  className="text-foreground max-w-[200px]"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">Driver pay = this rate × the load's miles, minus any fuel advance/lumper/late fee deductions.</p>
              </div>
            )}
            {!((form.pay_type === 'percentage' ? form.pay_percentage : form.pay_per_mile) > 0) && (
              <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mt-3">
                No rate set yet -- this driver's settlements will keep using the standard Carrier Take % formula until you set one.
              </p>
            )}
          </div>
        )}

        {/* Employment Dates */}
        {driver && (
          <div>
            <SectionHeader title="Employment" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Hire Date</label>
                <Input
                  type="date"
                  value={form.hire_date ?? ''}
                  onChange={e => set('hire_date', e.target.value)}
                  className="text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {/* This is about the hire_date FIELD being unset, not the
                      driver's actual employment status -- a driver can be
                      fully active (approved, driving loads) with no
                      hire_date recorded if they were made active through a
                      path other than the driver list's Hire action (e.g.
                      an older record, or a manual approval). Previous
                      wording ("Not yet hired") read as a claim about the
                      driver's status and contradicted an active driver's
                      own visible Active badge elsewhere in the app. */}
                  {form.hire_date ? '' : 'No hire date on file — set one here, or use the Hire action on the driver list.'}
                </p>
              </div>
              {form.termination_date && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Termination Date</label>
                  <Input type="date" value={form.termination_date} disabled className="text-foreground opacity-70" />
                  {form.termination_reason && (
                    <p className="text-xs text-muted-foreground mt-1">Reason: {form.termination_reason}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {saving ? 'Saving...' : driver ? 'Save Changes' : 'Add Driver'}
          </Button>
          <Button onClick={closeWithGuard} variant="outline" className="border-border">Cancel</Button>
        </div>
        </>
        )}

        {tab === 'dqf' && driver && (
          <div className="space-y-4">
            <DQFChecklist driver={driver} onSaved={(u) => onSaved(u)} />
            <Button onClick={closeWithGuard} variant="outline" className="border-border w-full">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}